import { ADDIN_NAMESPACE } from '../config/addinNamespace';

export type HardcodeScope = 'selection' | 'sheet' | 'workbook';

export interface HardcodeOptions {
  /** Convert every formula, not only this add-in's. Off by default. */
  allFormulas?: boolean;
}

export interface HardcodeResult {
  /** Formulas replaced by their current value; in a preview, those that would be. */
  replaced: number;
  /** Formulas left alone. */
  skipped: number;
  /**
   * Formulas to convert whose cell has no figure yet. Above zero, nothing is converted: frozen, the
   * cell would keep its marker for good, and so would every total computed from it.
   */
  noFigure: number;
}

/**
 * What a cell shows while its figure is still being fetched (#BUSY, #GETTING_DATA) or waits for a
 * sign-in (#SIGN_IN). Excel on the web reads a busy cell as "#BUSY" and desktop Excel as "#BUSY!".
 * This add-in's "#ERR: …" text is an answer, frozen like any other error.
 */
function hasNoFigureYet(value: unknown): boolean {
  return typeof value === 'string' && (value.startsWith('#BUSY') || value === '#GETTING_DATA' || value.startsWith('#SIGN_IN'));
}

/** Why nothing was converted. */
export function noFigureMessage(noFigure: number): string {
  return `${noFigure} cell(s) have no figure yet, so nothing was converted. Recalculate (or sign in), then convert.`;
}

/** The same, before converting. */
export function noFigurePreview(noFigure: number): string {
  return `${noFigure} cell(s) have no figure yet, so nothing would be converted. Recalculate (or sign in), then convert.`;
}

/**
 * Matches a call to one of this build's custom functions anywhere in a formula,
 * not just at the start. Excel stores them in several shapes and all have to be
 * caught — a cell we miss still shows #NAME? for whoever receives the workbook:
 *
 *   =BIFROST.LEDGER(...)          plain
 *   =@BIFROST.LEDGER(...)         implicit-intersection prefix Excel inserts
 *   =SUM(BIFROST.LEDGER(...))/12  nested inside a larger expression
 *   =_xludf.BIFROST.LEDGER(...)   how Excel writes a UDF it can't resolve
 *
 * The namespace is build-injected (BIFROST / BIFROST_TEST / BIFROST_DEV), so a
 * test build deliberately leaves prod formulas alone: it can't evaluate them,
 * and freezing their #NAME? error text would be worse than leaving them live.
 */
function buildFormulaPattern(namespace: string): RegExp {
  const escaped = namespace.replace(/[^A-Za-z0-9_]/g, '\\$&');
  return new RegExp(`(?:^|[^A-Za-z0-9_])${escaped}\\.`, 'i');
}

export class HardcodeService {
  private readonly pattern = buildFormulaPattern(ADDIN_NAMESPACE);

  /** Count what a hardcode would do, without changing the workbook. */
  async preview(scope: HardcodeScope, options: HardcodeOptions = {}): Promise<HardcodeResult> {
    return this.run(scope, options, false);
  }

  async hardcode(scope: HardcodeScope, options: HardcodeOptions = {}): Promise<HardcodeResult> {
    return this.run(scope, options, true);
  }

  private async run(
    scope: HardcodeScope,
    options: HardcodeOptions,
    write: boolean
  ): Promise<HardcodeResult> {
    let replaced = 0;
    let skipped = 0;
    let noFigure = 0;

    await Excel.run(async (context) => {
      const ranges = await this.getRanges(context, scope);

      for (const range of ranges) {
        range.load(['values', 'formulas']);
      }
      await context.sync();

      const writes: Array<{ range: Excel.Range; values: (string | number | boolean)[][] }> = [];
      for (const range of ranges) {
        if (range.isNullObject) continue;

        const formulas = range.formulas;
        const values = range.values;
        const newValues: (string | number | boolean)[][] = [];
        let hasChanges = false;

        for (let r = 0; r < formulas.length; r++) {
          newValues[r] = [];
          for (let c = 0; c < formulas[r].length; c++) {
            const formula = formulas[r][c];
            const isFormula = typeof formula === 'string' && formula.startsWith('=');
            const isTarget = isFormula && (options.allFormulas === true || this.pattern.test(formula));

            if (isTarget && hasNoFigureYet(values[r][c])) {
              newValues[r][c] = formulas[r][c];
              noFigure++;
            } else if (isTarget) {
              newValues[r][c] = values[r][c];
              replaced++;
              hasChanges = true;
            } else {
              newValues[r][c] = formulas[r][c];
              if (isFormula) skipped++;
            }
          }
        }

        if (hasChanges) writes.push({ range, values: newValues });
      }

      // One cell still without a figure refuses the whole conversion. In "every formula" mode every
      // formula is converted, Bifrost cells included, so a total over a cell still waiting refuses too.
      if (write && noFigure === 0) {
        // Written in one shot per range so a spilled array and its anchor are
        // replaced in the same operation rather than colliding mid-write.
        for (const { range, values } of writes) {
          range.formulas = values;
        }
        await context.sync();
      }
    });

    return { replaced: write && noFigure > 0 ? 0 : replaced, skipped, noFigure };
  }

  private async getRanges(
    context: Excel.RequestContext,
    scope: HardcodeScope
  ): Promise<Excel.Range[]> {
    if (scope === 'selection') {
      return [context.workbook.getSelectedRange()];
    }

    if (scope === 'sheet') {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      return [sheet.getUsedRangeOrNullObject()];
    }

    const worksheets = context.workbook.worksheets;
    worksheets.load('items');
    await context.sync();

    return worksheets.items.map(sheet => sheet.getUsedRangeOrNullObject());
  }
}
